<template>
	<div class="command-categories" style="height: 116px; min-height: 116px; max-height: 116px">
		<div v-for="category in categories" :key="category.title" class="category category-feature">
			<div class="category-actions feature-layout-grid" :style="getEvalutionGridStyle(category)" :data-feature-category="category.title">
				<div
					v-for="item in getEvalutionLayoutItems(category)"
					:key="item.action.key"
					class="feature-command"
					:class="{
						active: isEvalutionActionActive(item.action),
						'feature-command-large': item.size === 'large',
						'feature-command-small': item.size === 'small',
					}"
					:style="getEvalutionCellStyle(item)"
					:data-feature-key="item.action.key"
					:data-feature-size="item.size"
					:data-feature-mode="item.mode"
					:data-feature-row-span="String(item.rowSpan)"
				>
					<!-- 设计算例（带下拉，large column） -->
					<DropdownButton
						v-if="item.action.key === 'design-example'"
						:is-active="isDesignExampleActive"
						:disabled="Boolean(activeDesignExampleOption.disabled)"
						:icon="getDesignExampleIcon(activeDesignExampleOption)"
						:label="activeDesignExampleOption.label"
						:hint="{
							image: activeDesignExampleOption.hintImage,
							desc: activeDesignExampleOption.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						:options="designExampleDropdownOptions"
						@select="handleDesignExampleSelect"
						@click="handleDesignExampleClick"
					/>

					<!-- 检查活动文档（带下拉，small row，横向小按钮+右侧下拉） -->
					<DropdownButton
						v-else-if="item.action.key === 'check-active-document'"
						:is-active="isCheckActiveDocumentActive"
						:disabled="Boolean(activeCheckActiveDocumentOption.disabled)"
						:icon="activeCheckActiveDocumentOption.icon"
						:label="activeCheckActiveDocumentOption.label"
						:hint="{
							image: activeCheckActiveDocumentOption.hintImage,
							desc: activeCheckActiveDocumentOption.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						:options="checkActiveDocumentDropdownOptions"
						@select="handleCheckActiveDocumentSelect"
						@click="handleCheckActiveDocumentClick"
					/>

					<!-- 其他普通按钮（无下拉） -->
					<DropdownButton
						v-else
						:is-active="isCommandActive(item.action.command)"
						:disabled="Boolean(item.action.disabled)"
						:icon="item.action.icon"
						:label="item.action.label"
						:hint="{
							image: item.action.hintImage,
							desc: item.action.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						@click="emit('command', item.action.command || item.action.key)"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, h, ref, watch, type CSSProperties } from "vue";
	import { storeToRefs } from "pinia";
	import type { DropdownOption } from "naive-ui";
	import DropdownButton from "@/components/common/DropdownButton.vue";
	import SvgIcon from "@/components/common/SvgIcon.vue";
	import { useToolbarCommandStore } from "@/stores/toolbar-command";
	import evalutionDesignExampleIcon from "@/assets/icon/evalution_design_example.svg?raw";
	import evalutionInterferenceCheckIcon from "@/assets/icon/evalution_interference_check.svg?raw";
	import evalutionGapValidationIcon from "@/assets/icon/evalution_gap_validation.svg?raw";
	import evalutionHoleAlignmentIcon from "@/assets/icon/evalution_hole_alignment.svg?raw";
	import evalutionQualityAttributesIcon from "@/assets/icon/evalution_quality_attributes.svg?raw";
	import evalutionMeasurementIcon from "@/assets/icon/evalution_measurement.svg?raw";
	import evalutionAnnotationIcon from "@/assets/icon/evalution_annotation.svg?raw";
	import evalutionPerformanceEvaluationIcon from "@/assets/icon/evalution_performance_evaluation.svg?raw";
	import evalutionSensorIcon from "@/assets/icon/evalution_sensor.svg?raw";
	import evalutionSectionPropertiesIcon from "@/assets/icon/evalution_section_properties.svg?raw";
	import evalutionCurvatureIcon from "@/assets/icon/evalution_curvature.svg?raw";
	import evalutionSymmetryCheckIcon from "@/assets/icon/evalution_symmetry_check.svg?raw";
	import evalutionEntityComparisonIcon from "@/assets/icon/evalution_entity_comparison.svg?raw";
	import evalutionCompareDocumentsIcon from "@/assets/icon/evalution_compare_documents.svg?raw";
	import evalutionCheckActiveDocumentIcon from "@/assets/icon/evalution_check_active_document.svg?raw";
	import evalutionCheckAgainstExistingDocumentsIcon from "@/assets/icon/evalution_check_against_existing_documents.svg?raw";
	import evalutionCompilationCheckIcon from "@/assets/icon/evalution_compilation_check.svg?raw";
	import evalutionLearningAndApplicationCheckGuideIcon from "@/assets/icon/evalution_learning_and_application_check_guide.svg?raw";

	interface ToolbarAction {
		key: string;
		label: string;
		icon: string;
		command?: string;
		hint?: string;
		hintImage?: string;
		disabled?: boolean;
	}

	interface ToolbarOperation {
		name: string;
		hint: string;
	}

	type EvalutionLayoutCellSize = "small" | "large";
	type EvalutionLayoutCellMode = "row" | "column";

	interface EvalutionLayoutCell {
		key: string;
		column: number;
		row: number;
		rowSpan?: number;
		size?: EvalutionLayoutCellSize;
		mode?: EvalutionLayoutCellMode;
	}

	interface EvalutionLayout {
		columns: number;
		rows: number;
		cells: EvalutionLayoutCell[];
	}

	interface EvalutionLayoutItem {
		action: ToolbarAction;
		column: number;
		row: number;
		rowSpan: number;
		size: EvalutionLayoutCellSize;
		mode: EvalutionLayoutCellMode;
	}

	interface ToolbarCategory {
		title: string;
		actions: ToolbarAction[];
		featureLayout: EvalutionLayout;
	}

	type EvalutionDesignExampleModeKey = "suboption-design-example" | "parameter";
	type EvalutionCheckActiveDocumentModeKey =
		| "check-active-document"
		| "check-against-existing-documents"
		| "compilation-check"
		| "learning-and-application-check-guide";

	interface EvalutionCheckActiveDocumentModeOption {
		mode: EvalutionCheckActiveDocumentModeKey;
		label: string;
		icon: string;
		command: string;
		hint: string;
		hintImage?: string;
		disabled?: boolean;
	}

	interface EvalutionDesignExampleModeOption {
		mode: EvalutionDesignExampleModeKey;
		label: string;
		icon?: string;
		command: string;
		hint: string;
		hintImage?: string;
		disabled?: boolean;
	}

	const emit = defineEmits<{
		(e: "command", commandType: string): void;
		(e: "operationChange", operation: ToolbarOperation): void;
	}>();

	const toolbarCommandStore = useToolbarCommandStore();
	const { activeCommands } = storeToRefs(toolbarCommandStore);

	const categories: ToolbarCategory[] = [
		{
			title: "设计算例",
			actions: [
				{
					key: "design-example",
					label: "设计算例",
					icon: evalutionDesignExampleIcon,
					command: "design-example",
					hint: "设计算例",
				},
			],
			featureLayout: {
				columns: 1,
				rows: 3,
				cells: [{ key: "design-example", column: 1, row: 1, rowSpan: 3, size: "large", mode: "column" }],
			},
		},
		{
			title: "测量",
			actions: [
				{
					key: "interference-check",
					label: "干涉检查",
					icon: evalutionInterferenceCheckIcon,
					command: "interference-check",
					hint: "检测零部件之间的干涉。",
				},
				{
					key: "gap-validation",
					label: "间隙验证",
					icon: evalutionGapValidationIcon,
					command: "gap-validation",
					hint: "检查所选零部件之间或所选零部件与装配体其余部分之间的间隙。",
				},
				{
					key: "hole-alignment",
					label: "孔对齐",
					icon: evalutionHoleAlignmentIcon,
					command: "hole-alignment",
					hint: "检查装配体中的孔对齐。检查异型孔向导孔、简单孔和圆柱切割特征的对齐。",
				},
				{
					key: "quality-attributes",
					label: "质量属性",
					icon: evalutionQualityAttributesIcon,
					command: "quality-attributes",
					hint: "计算模型的质量特性，例如质量、体积和表面积。在您应用材料时还有其他选项可用，例如密度",
				},
				{
					key: "measurement",
					label: "测量",
					icon: evalutionMeasurementIcon,
					command: "measurement",
					hint: "计算所选项目之间的距离。",
					hintImage: "/images/operation/evalution-measurement.gif",
				},
				{
					key: "annotation",
					label: "标注",
					icon: evalutionAnnotationIcon,
					command: "annotation",
					hint: "在零件、装配体或工程图中创建或编辑标注。",
				},
				{
					key: "sensor",
					label: "传感器",
					icon: evalutionSensorIcon,
					command: "sensor",
					hint: "监视特定模型属性。在属性值与指定范围偏离时会发送警告。",
				},
				{
					key: "section-properties",
					label: "截面属性",
					icon: evalutionSectionPropertiesIcon,
					command: "section-properties",
					hint: "为位于平行平面中的多个面和草图评估截面属性。属性包括距原点的距离，以及关于质心和原点的惯性动量。",
				},
				{
					key: "performance-evaluation",
					label: "性能评估",
					icon: evalutionPerformanceEvaluationIcon,
					command: "performance-evaluation",
					hint: "分析装配体的性能并建议提高性能的操作。显示相应的零件、装配体、工程图统计数据，例如重建时间和零部件数\n" + "量。",
				},
			],
			featureLayout: {
				columns: 6,
				rows: 3,
				cells: [
					{ key: "interference-check", column: 1, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "gap-validation", column: 2, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "hole-alignment", column: 3, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "quality-attributes", column: 4, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "measurement", column: 5, row: 1, size: "small", mode: "row" },
					{ key: "annotation", column: 5, row: 2, size: "small", mode: "row" },
					{ key: "sensor", column: 5, row: 3, size: "small", mode: "row" },
					{ key: "section-properties", column: 6, row: 1, size: "small", mode: "row" },
					{ key: "performance-evaluation", column: 6, row: 2, size: "small", mode: "row" },
				],
			},
		},
		{
			title: "检查",
			actions: [
				{
					key: "curvature",
					label: "曲率",
					icon: evalutionCurvatureIcon,
					command: "curvature",
					hint: "根据曲率的局部半径以不同颜色显示零件或装配体。",
				},
				{
					key: "symmetry-check",
					label: "对称检查",
					icon: evalutionSymmetryCheckIcon,
					command: "symmetry-check",
					hint: "检查零件/装配体的对称。",
				},
				{
					key: "entity-comparison",
					label: "实体比较",
					icon: evalutionEntityComparisonIcon,
					command: "entity-comparison",
					hint: "显示所选实体的颜色比较。",
				},
			],
			featureLayout: {
				columns: 1,
				rows: 3,
				cells: [
					{ key: "curvature", column: 1, row: 1, size: "small", mode: "row" },
					{ key: "symmetry-check", column: 1, row: 2, size: "small", mode: "row" },
					{ key: "entity-comparison", column: 1, row: 3, size: "small", mode: "row" },
				],
			},
		},
		{
			title: "文档",
			actions: [
				{
					key: "compare-documents",
					label: "比较文档",
					icon: evalutionCompareDocumentsIcon,
					command: "compare-documents",
					hint: "比较两个文档",
				},
				{
					key: "check-active-document",
					label: "检查活动文档",
					icon: evalutionCheckActiveDocumentIcon,
					command: "check-active-document",
					hint: "检查活动文档",
				},
			],
			featureLayout: {
				columns: 1,
				rows: 3,
				cells: [
					{ key: "compare-documents", column: 1, row: 1, size: "small", mode: "row" },
					{ key: "check-active-document", column: 1, row: 2, size: "small", mode: "row" },
				],
			},
		}
	];

	const designExampleModeOptions: EvalutionDesignExampleModeOption[] = [
		{
			mode: "suboption-design-example",
			label: "设计算例",
			icon: evalutionDesignExampleIcon,
			command: "suboption-design-example",
			hint: "设计算例",
		},
		{
			mode: "parameter",
			label: "参数",
			command: "parameter",
			hint: "参数",
		},
	];

	/** 下拉菜单 option 图标尺寸；使用 inline style 避免 teleport 后 scoped 样式失效 */
	const dropdownOptionIconStyle = { width: "18px", height: "18px", objectFit: "contain" } as const;

	/**
	 * 构建工具栏下拉选项，无 icon 时不渲染图标节点。
	 *
	 * @param option 下拉选项配置。
	 * @returns Naive UI DropdownOption。
	 */
	function buildToolbarDropdownOption(option: { command: string; label: string; icon?: string }): DropdownOption {
		const dropdownOption: DropdownOption = {
			key: option.command,
			label: option.label,
		};
		if (option.icon) {
			dropdownOption.icon = () =>
				h(SvgIcon, {
					content: option.icon,
					alt: option.label,
					class: "dropdown-option-icon",
					style: dropdownOptionIconStyle,
				});
		}
		return dropdownOption;
	}

	/**
	 * 获取设计算例当前模式的按钮图标，无专属图标时回退到默认设计算例图标。
	 *
	 * @param option 设计算例模式选项。
	 * @returns 按钮图标 SVG 原始标记。
	 */
	function getDesignExampleIcon(option: EvalutionDesignExampleModeOption): string {
		return option.icon ?? evalutionDesignExampleIcon;
	}

	const checkActiveDocumentModeOptions: EvalutionCheckActiveDocumentModeOption[] = [
		{
			mode: "check-active-document",
			label: "检查激活的文档",
			icon: evalutionCheckActiveDocumentIcon,
			command: "check-active-document",
			hint: "检查激活的文档",
		},
		{
			mode: "check-against-existing-documents",
			label: "对照现有文件进行检查",
			icon: evalutionCheckAgainstExistingDocumentsIcon,
			command: "check-against-existing-documents",
			hint: "对照现有文件进行检查",
		},
		{
			mode: "compilation-check",
			label: "编制检查",
			icon: evalutionCompilationCheckIcon,
			command: "compilation-check",
			hint: "编制检查",
		},
		{
			mode: "learning-and-application-check-guide",
			label: "学用检查指南",
			icon: evalutionLearningAndApplicationCheckGuideIcon,
			command: "learning-and-application-check-guide",
			hint: "学用检查指南",
		},
	];

	// design-example 下拉支持（参考 FeatureToolbarTab.vue 模式）
	const designExampleModeMap = new Map(designExampleModeOptions.map(option => [option.mode, option]));
	const designExampleCommandMap = new Map(designExampleModeOptions.map(option => [option.command, option]));
	const activeDesignExampleMode = ref<EvalutionDesignExampleModeKey>("suboption-design-example");
	const activeDesignExampleOption = computed(() => designExampleModeMap.get(activeDesignExampleMode.value) ?? designExampleModeOptions[0]);
	const isDesignExampleActive = computed(() => activeCommands.value.some(command => command === "suboption-design-example" || command === "parameter"));
	const designExampleDropdownOptions = computed<DropdownOption[]>(() => designExampleModeOptions.map(option => buildToolbarDropdownOption(option)));

	// check-active-document 下拉支持（参考 FeatureToolbarTab.vue 模式）
	const checkActiveDocumentModeMap = new Map(checkActiveDocumentModeOptions.map(option => [option.mode, option]));
	const checkActiveDocumentCommandMap = new Map(checkActiveDocumentModeOptions.map(option => [option.command, option]));
	const activeCheckActiveDocumentMode = ref<EvalutionCheckActiveDocumentModeKey>("check-active-document");
	const activeCheckActiveDocumentOption = computed(
		() => checkActiveDocumentModeMap.get(activeCheckActiveDocumentMode.value) ?? checkActiveDocumentModeOptions[0]
	);
	const isCheckActiveDocumentActive = computed(() =>
		activeCommands.value.some(
			command =>
				command === "check-active-document" ||
				command === "check-against-existing-documents" ||
				command === "compilation-check" ||
				command === "learning-and-application-check-guide"
		)
	);
	const checkActiveDocumentDropdownOptions = computed<DropdownOption[]>(() =>
		checkActiveDocumentModeOptions.map(option => buildToolbarDropdownOption(option))
	);

	// apply 函数 + handler（参考 FeatureToolbarTab.vue 的 applyFeatureCornerCommand 模式）
	function applyDesignExampleCommand(command: string): void {
		const option = designExampleCommandMap.get(command);
		if (!option || option.disabled) return;
		activeDesignExampleMode.value = option.mode;
		emit("command", option.command);
		emit("operationChange", { name: option.label, hint: option.hint });
	}

	function handleDesignExampleSelect(key: string): void {
		applyDesignExampleCommand(key);
	}

	function handleDesignExampleClick(): void {
		applyDesignExampleCommand(activeDesignExampleOption.value.command);
	}

	function applyCheckActiveDocumentCommand(command: string): void {
		const option = checkActiveDocumentCommandMap.get(command);
		if (!option || option.disabled) return;
		activeCheckActiveDocumentMode.value = option.mode;
		emit("command", option.command);
		emit("operationChange", { name: option.label, hint: option.hint });
	}

	function handleCheckActiveDocumentSelect(key: string): void {
		applyCheckActiveDocumentCommand(key);
	}

	function handleCheckActiveDocumentClick(): void {
		applyCheckActiveDocumentCommand(activeCheckActiveDocumentOption.value.command);
	}

	function getEvalutionGridStyle(category: ToolbarCategory): CSSProperties {
		return {
			"--edit-component-grid-columns": String(category.featureLayout.columns),
			"--edit-component-grid-rows": String(category.featureLayout.rows),
		} as CSSProperties;
	}

	function getEvalutionLayoutItems(category: ToolbarCategory): EvalutionLayoutItem[] {
		return category.featureLayout.cells
			.map(cell => {
				const action = category.actions.find(candidate => candidate.key === cell.key);
				if (!action) {
					return null;
				}
				return {
					action,
					column: cell.column,
					row: cell.row,
					rowSpan: cell.rowSpan ?? 1,
					size: cell.size ?? "small",
					mode: cell.mode ?? "column",
				} satisfies EvalutionLayoutItem;
			})
			.filter((item): item is EvalutionLayoutItem => item !== null);
	}

	function isEvalutionActionActive(action: ToolbarAction): boolean {
		if (action.key === "design-example") {
			return isDesignExampleActive.value;
		}
		if (action.key === "check-active-document") {
			return isCheckActiveDocumentActive.value;
		}
		return isCommandActive(action.command);
	}

	/**
	 * 判断指定命令是否处于激活状态。
	 *
	 * @param command 编辑器当前要匹配的命令名。
	 * @returns 命令是否已在激活命令集合中。
	 */
	function isCommandActive(command: string): boolean {
		return activeCommands.value.includes(command);
	}

	function getEvalutionCellStyle(item: EvalutionLayoutItem): CSSProperties {
		return {
			gridColumn: String(item.column),
			gridRow: `${item.row} / span ${item.rowSpan}`,
		};
	}
</script>

<style scoped lang="less">
	.command-categories {
		display: flex;
		align-items: stretch;
		gap: 0;
		flex-wrap: nowrap;
		width: 100%;
		height: 116px;
		min-height: 116px;
		max-height: 116px;
		box-sizing: border-box;
		overflow-x: auto;
	}

	.category {
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 4px 8px;
		min-width: max-content;
		position: relative;
	}

	.category + .category {
		border-left: 1px solid var(--n-tab-border-color);
	}

	.category-actions.feature-layout-grid {
		--feature-large-width: 66px;
		--feature-small-min-width: 62px;
		--feature-row-height: 34px;
		display: grid;
		grid-template-columns: repeat(var(--feature-grid-columns), max-content);
		grid-template-rows: repeat(var(--feature-grid-rows), var(--feature-row-height));
		align-items: stretch;
		justify-items: stretch;
		column-gap: 3px;
		row-gap: 3px;
	}

	.feature-command {
		width: max-content;
		height: 100%;
	}

	.feature-command-small {
		width: max-content;
		min-width: var(--feature-small-min-width);
	}

	.feature-command-large {
		width: var(--feature-large-width);
	}

	.feature-command > * {
		width: 100%;
	}

	:deep(.dropdown-option-icon) {
		width: 18px;
		height: 18px;
		object-fit: contain;
	}
</style>